import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ArialNormal from "../../../utils/Customfonts/ARIAL.TTF";
import ArialBold from "../../../utils/Customfonts/ARIALBD.TTF";
import ArialItalic from "../../../utils/Customfonts/ARIALI.TTF";
import ArialBoldItalic from "../../../utils/Customfonts/ARIALBI.TTF";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Arial",
  normalFont: "Arial",
  italic: "italic",
  italicFont: "Arial",
};

// -----------------------------------------------------------------------------
// GLOBAL LAYOUT CONFIG
// -----------------------------------------------------------------------------
// `contentPadding` is declared once here: it is the gap between the outer
// page border and every table / section in the document. `margin` is
// derived from it, so changing this single number resizes every table on
// every page automatically (full width, dynamic).
// -----------------------------------------------------------------------------

export const config = {
  borderMargin: 15,
  contentPadding: 20, // <-- 20px gap between outer border and all tables/content

  get margin() {
    return this.borderMargin + this.contentPadding;
  },

  fontSize: 9.5,
  fontSizeSm: 8.5,
  fontSizeLg: 11,
  fontSizeTitle: 18,

  cellPadding: 5,

  sectionGap: 22,
  lineGap: 20,

  // Table header styling (Employment History / Professional Qualifications /
  // Work Experience). Adjust these in one place to restyle every header.
  headerFontSize: 9.5,
  headerFont: customFontFamily.boldFont,
  headerFontStyle: customFontFamily.bold,
  headerAlign: "center",
  headerPadding: { top: 8, right: 6, bottom: 8, left: 6 },

  get contentTop() {
    return this.borderMargin + 65;
  },

  get contentBottom() {
    return this.borderMargin + 25;
  },
};

// -----------------------------------------------------------------------------
// MASTER OPTION LISTS (fixed form fields — always printed in full,
// checked/unchecked based on whatever is present in `data`)
// -----------------------------------------------------------------------------
// Order matters: options are laid out row-major across `columns` (3) but
// interleaved so that reading down each printed column gives the natural
// grouping shown on the reference CV.
// -----------------------------------------------------------------------------

export const COMPETENCY_OPTIONS = {
  fieldOfActivities: [
    "Oil & Gas",
    "Biomass Power",
    "Construction",
    "Nuclear Power",
    "Solar Energy",
    "Transportation",
    "Fossil Power",
    "Wind Energy",
    "Environmental",
  ],

  discipline: [
    "Mechanical",
    "Metallurgical",
    "Chemical",
    "Electrical",
    "Processing",
    "Civil",
  ],

  expertiseInService: [
    "Performance Tests",
    "Audit",
    "Fire Protection",
    "Surveillance",
    "NDT",
    "ATEX",
    "On site supervision",
    "Welding",
    "Painting / Coating",
    "Vendor Assessment",
    "HSE",
    "Risk & Safety",
  ],

  expertiseInEquipment: [
    "Steel Mill Products- Pipe & Plate",
    "Pipe, Fittings, Flanges & Bulk Items",
    "Skid",
    "Pressure Equipment",
    "Electrical Equipment/ Cables",
    "Subsea Manifolds",
    "Valves & Actuators",
    "Instrumentation & Control Systems",
    "Structures",
    "Heat Exchangers",
    "Hazardous Area Equipment",
    "Lifting Equipment",
    "Pumps",
    "Mechanical Rotating Equipment",
    "GRP Fabrications – Glass Fibre Reinforced Plastics",
  ],
};

// -----------------------------------------------------------------------------
// COMMON STYLES
// -----------------------------------------------------------------------------

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: [225, 225, 225],
  textColor: [0, 0, 0],
  valign: "middle",
});

const getDarkLabelStyles = () => ({
  ...getBaseStyles(),
  font: config.headerFont,
  fontStyle: config.headerFontStyle,
  fontSize: config.headerFontSize,
  fillColor: [55, 55, 55],
  textColor: [255, 255, 255],
  halign: config.headerAlign,
  valign: "middle",
  cellPadding: config.headerPadding,
});

// -----------------------------------------------------------------------------
// TEXT NORMALIZATION (so "Steel Mill Products - Pipe & Plate" in state still
// matches "Steel Mill Products- Pipe & Plate" in the master option list)
// -----------------------------------------------------------------------------

const normalizeText = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/\s*-\s*/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const isOptionSelected = (option, selectedList) => {
  const target = normalizeText(option);
  return (Array.isArray(selectedList) ? selectedList : []).some(
    (item) => normalizeText(item) === target
  );
};

// -----------------------------------------------------------------------------
// LAYOUT MANAGER
// -----------------------------------------------------------------------------

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;

    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();

    // Full width available for every table: page width minus the border
    // margin and the content padding on BOTH sides.
    this.contentWidth = this.pageWidth - config.margin * 2;

    this.y = config.contentTop;
  },

  advance(amount) {
    this.y += amount;
  },

  ensureSpace(requiredHeight = 40) {
    if (this.y + requiredHeight > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },

  afterTable(gap = config.sectionGap) {
    if (this.doc.lastAutoTable) {
      this.y = this.doc.lastAutoTable.finalY + gap;
    }
  },
};

// -----------------------------------------------------------------------------
// FONT LOADING
// -----------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer) => {
  let binary = "";

  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;

  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }

  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, base64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (error) {
    console.error(`Font loading failed: ${fontName}`, error);
  }
};

// -----------------------------------------------------------------------------
// HEADER / FOOTER
// -----------------------------------------------------------------------------

const addPageDecorators = (doc, totalPages, data = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    // OUTER BORDER
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.7);
    doc.rect(
      config.borderMargin,
      config.borderMargin,
      pageWidth - config.borderMargin * 2,
      pageHeight - config.borderMargin * 2
    );

    // COMPANY NAME
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(18);
    doc.setTextColor(25, 25, 25);
    doc.text(
      data.companyTitle || "DYC GLOBAL PRIVATE LIMITED",
      pageWidth / 2,
      config.borderMargin + 25,
      { align: "center" }
    );

    // CV TITLE
    doc.setFontSize(14);
    doc.text("CURRICULUM VITAE", pageWidth / 2, config.borderMargin + 45, {
      align: "center",
    });

    // LOGO
    try {
      const logoSize = 45;
      const logoX = pageWidth - config.borderMargin - logoSize - 8;
      const logoY = config.borderMargin + 5;

      try {
        doc.addImage(DYC_LOGO, "WEBP", logoX, logoY, logoSize, logoSize);
      } catch (_) {
        try {
          doc.addImage(DYC_LOGO, "PNG", logoX, logoY, logoSize, logoSize);
        } catch (__) {
          doc.addImage(DYC_LOGO, logoX, logoY, logoSize, logoSize);
        }
      }
    } catch (_) {
      // Logo is optional
    }

    // HEADER LINE
    doc.setLineWidth(0.5);
    doc.line(
      config.borderMargin,
      config.borderMargin + 55,
      pageWidth - config.borderMargin,
      config.borderMargin + 55
    );

    // FOOTER
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(7.5);
    doc.text(
      "Company General Use. All Rights Reserved By DYC Global Pvt. Ltd.",
      config.borderMargin + 8,
      pageHeight - config.borderMargin - 8
    );

    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.text(
      `Page ${page} of ${totalPages}`,
      pageWidth - config.borderMargin - 60,
      pageHeight - config.borderMargin - 8
    );
  }
};

// -----------------------------------------------------------------------------
// CHECKBOX
// -----------------------------------------------------------------------------

const drawCheckbox = (doc, x, y, checked = false, label = "", fontSize = 9) => {
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.7);
  doc.rect(x, y - 7, 8, 8);

  if (checked) {
    doc.setLineWidth(1.1);
    doc.line(x + 1.5, y - 3, x + 3.5, y - 1);
    doc.line(x + 3.5, y - 1, x + 7, y - 6);
    doc.setLineWidth(0.7);
  }

  if (label) {
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(fontSize);
    doc.text(label, x + 14, y);
  }
};

// -----------------------------------------------------------------------------
// SECTION TITLE
// -----------------------------------------------------------------------------

const drawSectionTitle = (doc, title, y) => {
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(10.5);
  doc.setTextColor(0, 0, 0);
  doc.text(title, config.margin, y);
};

// -----------------------------------------------------------------------------
// LABEL / VALUE
// -----------------------------------------------------------------------------

const drawInfoRow = (doc, label, value, y, labelWidth = 150) => {
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(9.5);
  doc.text(label, config.margin + 10, y);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.text(":", config.margin + labelWidth + 5, y);

  const valueX = config.margin + labelWidth + 18;
  const maxWidth = layout.pageWidth - valueX - config.margin - 15;

  const valueLines = doc.splitTextToSize(String(value || ""), maxWidth);
  doc.text(valueLines, valueX, y);

  return Math.max(17, valueLines.length * 13);
};

// -----------------------------------------------------------------------------
// INTRODUCTION SECTION
// -----------------------------------------------------------------------------

const drawIntroduction = (doc, data) => {
  drawSectionTitle(doc, "Introduction:", layout.y);
  layout.advance(27);

  const photoBoxW = 95;
  const photoBoxH = 115;
  const photoX = layout.pageWidth - config.margin - photoBoxW - 10;
  const photoY = layout.y - 5;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.8);
  doc.rect(photoX, photoY, photoBoxW, photoBoxH);

  if (data.photo) {
    try {
      doc.addImage(data.photo, photoX + 5, photoY + 5, photoBoxW - 10, photoBoxH - 10);
    } catch (error) {
      console.warn("Unable to add candidate photo", error);
    }
  }

  const textRows = [
    ["Name", data.name],
    ["Present Position", data.position],
    ["Location", data.location],
    ["Experience", data.experienceYears],
    ["Educational Qualification", data.education],
    ["Awareness of standards", data.standards],
    ["Languages Known", data.languages],
  ];

  textRows.forEach(([label, value]) => {
    const rowHeight = drawInfoRow(doc, label, value, layout.y, 145);
    layout.advance(rowHeight);
  });

  layout.advance(18);
};

// -----------------------------------------------------------------------------
// CHECKBOX GRID TABLE (generic, full width, dynamic)
// -----------------------------------------------------------------------------

const drawCheckboxTable = (doc, label, options, selectedOptions, opts = {}) => {
  const { columns = 3, minCellHeight = 30, labelWidthRatio = 0.28 } = opts;

  const rowCount = Math.ceil(options.length / columns);
  const rows = [];

  const CHECKBOX_LEFT_PAD = 26; // room for the checkbox glyph before the text starts
  const CELL_RIGHT_PAD = 6;

  for (let row = 0; row < rowCount; row++) {
    const rowData = [];

    // The label is a single cell that spans every row of this group — only
    // supplied once, on the first row, with rowSpan. Later rows omit it
    // entirely, so it visually merges into one tall dark cell (matches the
    // reference CV) instead of repeating on every line.
    if (row === 0) {
      rowData.push({
        content: label,
        rowSpan: rowCount,
        styles: {
          fillColor: [55, 55, 55],
          textColor: [255, 255, 255],
          fontStyle: customFontFamily.bold,
          font: customFontFamily.boldFont,
          halign: "left",
          valign: "middle",
        },
      });
    }

    for (let column = 0; column < columns; column++) {
      const index = row * columns + column;
      const option = options[index] || "";

      rowData.push({
        option,
        checked: option ? isOptionSelected(option, selectedOptions) : false,
      });
    }

    rows.push(rowData);
  }

  const labelWidth = layout.contentWidth * labelWidthRatio;
  const optionColWidth = (layout.contentWidth - labelWidth) / columns;

  const columnStyles = {
    0: { cellWidth: labelWidth },
  };

  for (let c = 1; c <= columns; c++) {
    columnStyles[c] = {
      cellWidth: optionColWidth,
      cellPadding: {
        top: 7,
        bottom: 7,
        left: CHECKBOX_LEFT_PAD,
        right: CELL_RIGHT_PAD,
      },
    };
  }

  autoTable(doc, {
    startY: layout.y,
    body: rows,
    theme: "grid",

    styles: {
      ...getBaseStyles(),
      minCellHeight,
      fontSize: 9,
    },

    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },

    columnStyles,

    // Only wrap/prepare the option text here — the label cell already has
    // its final `content`, so it needs no extra handling. We do NOT draw
    // the option text ourselves elsewhere; the default renderer draws it
    // (indented via cellPadding.left above), and didDrawCell below only
    // adds the checkbox graphic on top. This avoids double-drawn text.
    didParseCell: (hookData) => {
      if (hookData.section !== "body") return;
      if (hookData.column.index === 0) return; // label cell, already set

      const cellData = hookData.cell.raw;

      // IMPORTANT: hookData.cell.width is NOT finalized yet at this stage
      // (column layout runs after didParseCell). Using it here produces a
      // near-zero width, which makes splitTextToSize wrap one character
      // per line. Use the column width we already computed ourselves.
      const availableWidth = optionColWidth - CHECKBOX_LEFT_PAD - CELL_RIGHT_PAD;

      hookData.cell.text = doc.splitTextToSize(cellData?.option || "", availableWidth);
      hookData.cell.styles.fontStyle = customFontFamily.bold;
      hookData.cell.styles.font = customFontFamily.boldFont;
    },

    didDrawCell: (hookData) => {
      if (hookData.section !== "body") return;
      if (hookData.column.index === 0) return; // label cell has no checkbox

      const cellData = hookData.cell.raw;
      if (!cellData?.option) return;

      const checkboxX = hookData.cell.x + 8;
      const checkboxY = hookData.cell.y + hookData.cell.height / 2 + 3;

      drawCheckbox(doc, checkboxX, checkboxY, cellData.checked, "", 8);
    },
  });
};

// -----------------------------------------------------------------------------
// COMPETENCY SUMMARY
// -----------------------------------------------------------------------------
// All four groups (Field of Activities, Discipline, Expertise in Service,
// Expertise in Equipment) are printed as ONE continuous checkbox grid,
// exactly like the reference CV. Every option in COMPETENCY_OPTIONS is
// always shown; it gets a tick mark only if it's present in the matching
// array on `data`.
// -----------------------------------------------------------------------------

const drawCompetencySummary = (doc, data) => {
  layout.ensureSpace(100);

  drawSectionTitle(doc, "Competency Summary:", layout.y);
  layout.advance(28);

  drawCheckboxTable(
    doc,
    "Field of Activities:",
    COMPETENCY_OPTIONS.fieldOfActivities,
    data.fieldOfActivities || []
  );
  layout.afterTable(0);

  drawCheckboxTable(
    doc,
    "Discipline:",
    COMPETENCY_OPTIONS.discipline,
    data.discipline || []
  );
  layout.afterTable(0);

  drawCheckboxTable(
    doc,
    "Expertise in Service:",
    COMPETENCY_OPTIONS.expertiseInService,
    data.expertiseInService || [],
    { minCellHeight: 26 }
  );
  layout.afterTable(0);

  drawCheckboxTable(
    doc,
    "Expertise in Equipment:",
    COMPETENCY_OPTIONS.expertiseInEquipment,
    data.expertiseInEquipment || [],
    { minCellHeight: 34 } // extra height: several long, two-line labels
  );
  layout.afterTable(18);
};

// -----------------------------------------------------------------------------
// EMPLOYMENT HISTORY
// -----------------------------------------------------------------------------

const drawEmploymentHistory = (doc, data) => {
  layout.ensureSpace(130);

  drawSectionTitle(doc, "A. Employment History:", layout.y);
  layout.advance(14);

  const history = data.employmentHistory || [];

  const rows = history.map((item) => [
    item.Period || "",
    item.Organization || "",
    item["Responsibility/Position"] || "",
  ]);

  while (rows.length < 1) {
    rows.push(["", "", ""]);
  }

  autoTable(doc, {
    startY: layout.y,
    head: [["Period", "Organization", "Responsibility/Position"]],
    body: rows,
    theme: "grid",
    styles: getBaseStyles(),
    headStyles: getDarkLabelStyles(),

    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },

    columnStyles: {
      0: { cellWidth: layout.contentWidth * 0.2 },
      1: { cellWidth: layout.contentWidth * 0.3 },
      2: { cellWidth: layout.contentWidth * 0.5 },
    },
  });

  layout.afterTable(20);
};

// -----------------------------------------------------------------------------
// PROFESSIONAL QUALIFICATIONS
// -----------------------------------------------------------------------------

const drawProfessionalQualifications = (doc, data) => {
  layout.ensureSpace(150);

  drawSectionTitle(doc, "B. Professional Qualifications:", layout.y);
  layout.advance(14);

  const qualifications = data.professionalQualifications || [];

  const rows = qualifications.map((item) => [
    item.NDT || "",
    item.Painting || "",
    item.QMS || "",
    item.Statutory || "",
    item.Others || "",
  ]);

  while (rows.length < 1) {
    rows.push(["", "", "", "", ""]);
  }

  const colWidth = layout.contentWidth / 5;

  autoTable(doc, {
    startY: layout.y,
    head: [["NDT", "Painting", "QMS", "Statutory", "Others"]],
    body: rows,
    theme: "grid",
    styles: getBaseStyles(),
    headStyles: getDarkLabelStyles(),

    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },

    columnStyles: {
      0: { cellWidth: colWidth },
      1: { cellWidth: colWidth },
      2: { cellWidth: colWidth },
      3: { cellWidth: colWidth },
      4: { cellWidth: colWidth },
    },
  });

  layout.afterTable(20);
};

// -----------------------------------------------------------------------------
// WORK EXPERIENCE
// -----------------------------------------------------------------------------

const drawWorkExperience = (doc, data) => {
  layout.ensureSpace(150);

  drawSectionTitle(doc, "C. Work Experience:", layout.y);
  layout.advance(14);

  const workExperience = data.workExperience || [];

  const rows = workExperience.map((item) => [
    item.Organization || "",
    item.Position || "",
    item["Brief Description of Work Experience"] || "",
  ]);

  while (rows.length < 1) {
    rows.push(["", "", ""]);
  }

  autoTable(doc, {
    startY: layout.y,
    head: [["Organization", "Position", "Brief Description of Work Experience"]],
    body: rows,
    theme: "grid",
    styles: getBaseStyles(),
    headStyles: getDarkLabelStyles(),

    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },

    columnStyles: {
      0: { cellWidth: layout.contentWidth * 0.22 },
      1: { cellWidth: layout.contentWidth * 0.2 },
      2: { cellWidth: layout.contentWidth * 0.58 },
    },
  });

  layout.afterTable(20);
};

// -----------------------------------------------------------------------------
// MAIN PDF GENERATOR
// -----------------------------------------------------------------------------

export const generateCurriculumVitaePDF = async (data = {}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  await Promise.all([
    loadAndAddFont(
      doc,
      ArialNormal,
      "Arial-Normal",
      customFontFamily.normalFont,
      customFontFamily.normal
    ),
    loadAndAddFont(
      doc,
      ArialBold,
      "Arial-Bold",
      customFontFamily.boldFont,
      customFontFamily.bold
    ),
    loadAndAddFont(
      doc,
      ArialItalic,
      "Arial-Italic",
      customFontFamily.italicFont,
      customFontFamily.italic
    ),
    loadAndAddFont(
      doc,
      ArialBoldItalic,
      "Arial-BoldItalic",
      customFontFamily.normalFont,
      "bolditalic"
    ),
  ]);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

  layout.init(doc);
  layout.y = config.contentTop + 10;

  drawIntroduction(doc, data);
  drawCompetencySummary(doc, data); // Field of Activities + Discipline + Expertise in Service + Expertise in Equipment
  drawEmploymentHistory(doc, data);
  drawProfessionalQualifications(doc, data);
  drawWorkExperience(doc, data);

  const totalPages = doc.internal.getNumberOfPages();
  addPageDecorators(doc, totalPages, data);

  return doc;
};
